<script setup>
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  blockers: { type: Array, default: () => [] },
  warnings: { type: Array, default: () => [] },
});

const emit = defineEmits(['review']);
const { t: ce } = useI18n();
</script>

<template>
  <div class="flex flex-col gap-3">
    <section
      v-if="blockers.length"
      class="rounded-xl border border-n-ruby-5 bg-n-ruby-3/40 p-4"
      role="alert"
      aria-live="assertive"
    >
      <div class="flex items-start gap-3">
        <span
          class="i-lucide-circle-alert mt-0.5 size-5 shrink-0 text-n-ruby-11"
          aria-hidden="true"
        />
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.PREFLIGHT_BLOCKERS_TITLE') }}
          </h3>
          <p class="mt-1 text-xs leading-relaxed text-n-slate-11">
            {{
              ce('CAPTAIN_EXPERIENCE.WORKSPACE.PREFLIGHT_BLOCKERS_DESCRIPTION')
            }}
          </p>
          <ul class="mt-3 flex flex-col gap-2">
            <li
              v-for="blocker in blockers"
              :key="`${blocker.key}-${blocker.message}`"
              class="flex flex-col gap-2 rounded-lg border border-n-ruby-5/70 bg-n-solid-1 p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <p class="text-sm leading-relaxed text-n-slate-12">
                {{ blocker.message }}
              </p>
              <Button
                type="button"
                color="slate"
                variant="faded"
                size="sm"
                class="shrink-0 self-start sm:self-auto"
                icon="i-lucide-arrow-right"
                :label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.PREFLIGHT_REVIEW')"
                @click="emit('review', blocker)"
              />
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section
      v-if="warnings.length"
      class="rounded-xl border border-n-amber-5 bg-n-amber-3/40 p-4"
      role="status"
      aria-live="polite"
    >
      <div class="flex items-start gap-3">
        <span
          class="i-lucide-triangle-alert mt-0.5 size-5 shrink-0 text-n-amber-11"
          aria-hidden="true"
        />
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.PREFLIGHT_WARNINGS_TITLE') }}
          </h3>
          <p class="mt-1 text-xs leading-relaxed text-n-slate-11">
            {{
              ce('CAPTAIN_EXPERIENCE.WORKSPACE.PREFLIGHT_WARNINGS_DESCRIPTION')
            }}
          </p>
          <ul class="mt-3 flex flex-col gap-2">
            <li
              v-for="warning in warnings"
              :key="`${warning.key}-${warning.message}`"
              class="flex flex-col gap-2 rounded-lg border border-n-amber-5/70 bg-n-solid-1 p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <p class="text-sm leading-relaxed text-n-slate-12">
                {{ warning.message }}
              </p>
              <Button
                type="button"
                color="slate"
                variant="ghost"
                size="sm"
                class="shrink-0 self-start sm:self-auto"
                icon="i-lucide-arrow-right"
                :label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.PREFLIGHT_REVIEW')"
                @click="emit('review', warning)"
              />
            </li>
          </ul>
        </div>
      </div>
    </section>
  </div>
</template>
